import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@west-tennessee-consulting/ui";
import { useQuery as useApolloQuery, useMutation as useApolloMutation } from "@apollo/client/react";

// Types for backward compatibility
export interface MspDocument {
  id: string;
  title: string;
  content: string | null;
  /** Set only once content has been offloaded to S3 (content-offload.ts) — content is then null. */
  content_s3_key?: string | null;
  /** Short snippet kept in Postgres after offload — the web reader's fallback when content is null. */
  content_preview?: string | null;
  excerpt?: string | null;
  category: string;
  tags?: string[] | null;
  client_id?: string | null;
  company_id?: string | null;
  difficulty?: string | null;
  is_internal: boolean;
  is_active: boolean;
  is_featured: boolean;
  estimated_read_time?: number | null;
  last_accessed_at?: string | null;
  access_count: number;
  created_by: string;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
  shared_with_users?: string[] | null;
  company?: {
    id: string;
    name: string;
    industry?: string | null;
    website?: string | null;
    city?: string | null;
    state?: string | null;
  } | null;
  client?: {
    id: string;
    name: string;
    company_name?: string | null;
    email?: string | null;
  } | null;
  creator?: {
    id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  updater?: {
    id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}

export interface CreateMspDocumentData {
  title: string;
  content: string;
  excerpt?: string;
  category:
    | "procedures"
    | "technical"
    | "best-practices"
    | "client-specific"
    | "troubleshooting"
    | "templates";
  tags?: string[];
  client_id?: string;
  company_id?: string;
  difficulty?: "beginner" | "intermediate" | "advanced";
  is_internal?: boolean;
  is_featured?: boolean;
  estimated_read_time?: number;
  shared_with_users?: string[];
}

export interface UpdateMspDocumentData extends Partial<CreateMspDocumentData> {}
import {
  GetMspDocumentssDocument,
  GetMspDocumentsDocument,
  DeleteMspDocumentsDocument,
  type GetMspDocumentssQuery,
  type GetMspDocumentsQuery,
  type DeleteMspDocumentsMutation,
  type GetMspDocumentssQueryVariables,
} from "@/src/gql/graphql";

/**
 * Message off an unknown `catch` binding, without pretending it is an Error.
 * Exported for `msp-docs-graphql-write.ts` (create/update split out of this
 * file on module-size grounds — MERIDIAN_SURFACE_PARITY_AND_MODULE_SIZE), so
 * there is one copy rather than two drifting ones.
 */
export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export { useCreateMspDocument, useUpdateMspDocument } from "./msp-docs-graphql-write";

// Query keys for React Query
export const mspDocsKeys = {
  all: ["msp-docs"] as const,
  lists: () => [...mspDocsKeys.all, "list"] as const,
  list: (filters: unknown) => [...mspDocsKeys.lists(), filters] as const,
  details: () => [...mspDocsKeys.all, "detail"] as const,
  detail: (id: string) => [...mspDocsKeys.details(), id] as const,
};

/**
 * Get MSP Documents list with pagination and filtering
 */
export function useGetMspDocuments(
  variables: {
    first?: number;
    after?: string;
    filter?: GetMspDocumentssQueryVariables["filter"];
    skip?: boolean;
  } = {}
) {
  const { data, loading, error } = useApolloQuery<GetMspDocumentssQuery>(GetMspDocumentssDocument, {
    variables: {
      first: variables.first || 20,
      after: variables.after,
      filter: variables.filter,
    },
    skip: variables.skip,
  });

  return {
    data,
    isLoading: loading,
    error: error ? new Error(error.message) : null,
  };
}

/**
 * Get single MSP Document by ID
 */
export function useGetMspDocument(id: string) {
  const { data, loading, error } = useApolloQuery<GetMspDocumentsQuery>(GetMspDocumentsDocument, {
    variables: { id },
    skip: !id,
  });

  return {
    data: data?.msp_documentsCollection?.edges?.[0]?.node as MspDocument,
    isLoading: loading,
    error: error ? new Error(error.message) : null,
  };
}

/**
 * Delete MSP Document
 */
export function useDeleteMspDocument() {
  const { toast } = useToast();
  const [deleteMspDocument, { loading }] = useApolloMutation<DeleteMspDocumentsMutation>(
    DeleteMspDocumentsDocument
  );

  const mutateAsync = async (id: string) => {
    try {
      await deleteMspDocument({
        variables: { id },
        refetchQueries: ["GetMspDocumentss"],
      });

      toast({
        title: "Document Deleted",
        description: "Document has been deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: errorMessage(error),
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    mutateAsync,
    isPending: loading,
  };
}
